<template>
  <div v-if="readPermission" class="tenantManagementContainer">
    <!-- Header Section like DocumentPreview -->
    <div class="document-preview-header" style="margin-bottom: 20px; background: #fff; border-radius: 8px; border: 1px solid #deecf7">
      <!-- Top Row: Classified as + Source Document + Save and Next Button -->
      <div class="header-row" style="display: flex; justify-content: space-between; align-items: center; padding: 12px 18px; border-bottom: 1px solid #e0e0e0; gap: 16px">
        <div class="header-left" style="display: flex; align-items: center; gap: 18px">
          <div>
            <div class="header-label" style="font-size: 13px; font-family: 'Archivo', Arial, sans-serif; color: #757575">{{ $t('extractionFields.classifiedAs') }}</div>
            <div class="header-value" style="font-size: 15px; font-family: 'Archivo', Arial, sans-serif; text-transform: capitalize; line-height: 1; font-weight: 500">{{ getDocumentTypeName('class') }}</div>
          </div>
          <div class="vertical-divider" style="width: 1px; height: 32px; background: #e0e0e0; margin: 0 10px"></div>
          <div>
            <div class="header-label" style="font-size: 13px; font-family: 'Archivo', Arial, sans-serif; color: #757575">{{ $t('extractionFields.sourceDocument') }}</div>
            <div class="header-value" style="font-size: 15px; font-family: 'Archivo', Arial, sans-serif; text-transform: capitalize; line-height: 1; font-weight: 500">{{ getSourceDocumentName() }}</div>
          </div>
        </div>
        <!-- Save and Next Button -->
        <button v-if="isStepMode" @click="handleComplete" :disabled="!selectedValues.vasionFormId" :class="['blueprint-btn', { 'blueprint-btn-disabled': !selectedValues.vasionFormId }]">{{ selectedTenantUsesBedrock ? $t('extractionFields.next') : $t('extractionFields.saveAndNext') }}</button>
      </div>

      <!-- Bottom Row: Document Type and Vasion Form (50/50 width) -->
      <div class="form-row" style="display: flex; padding: 16px 18px; gap: 20px">
        <div class="form-field" style="flex: 1">
          <div class="header-label" style="font-size: 13px; font-family: 'Archivo', Arial, sans-serif; color: #757575; margin-bottom: 5px">{{ $t('extractionFields.documentType') }}</div>
          <input type="text" v-model="editableDocumentTypeName" @input="handleDocumentTypeNameChange" class="document-type-input" style="width: 100%; border: 1px solid #ddd; padding: 8px 12px; border-radius: 4px; background: #fff; color: #333; cursor: text; font-size: 14px" placeholder="Enter document type name" />
        </div>
        <div class="form-field" style="flex: 1">
          <div class="header-label" style="font-size: 13px; font-family: 'Archivo', Arial, sans-serif; color: #757575; margin-bottom: 5px">{{ $t('extractionFields.forms.labels.vasion_form') }}</div>
          <select v-model="selectedValues.vasionFormId" @change="handleVasionFormChange" class="custom-select" style="width: 100%; padding: 8px 12px; border-radius: 4px; border: 1px solid #ddd; font-size: 14px">
            <option value="">{{ $t('extractionFields.forms.placeholders.select_vasion_form') }}</option>
            <option v-for="form in vasionForms" :key="form.id" :value="form.id">
              {{ form.vasion_form_id }}
            </option>
          </select>
        </div>
      </div>
    </div>

    <div class="configurePageContainer" style="min-height: 100vh; width: 100%; margin-bottom: 20px">
      <div v-if="loading" style="position: absolute; top: 50%; left: 65%; transform: translate(-50%, -50%); display: flex; justify-content: center; align-items: center">
        <div class="spinner-border text-primary spinner-grow-xl" roleCategories="status"></div>
      </div>
      <div class="customRowTenant">
        <p class="jobHistoryTitle" style="text-align: left">{{ $t('extractionFields.title') }}</p>
      </div>

      <!-- AI Method and Search Controls -->
      <div class="tableHeadingsNew" style="display: flex; align-items: flex-start; justify-content: space-between; margin-top: 20px; gap: 20px">
        <!-- AI Method Selector -->
        <div style="display: flex; flex-direction: column; align-items: flex-start; gap: 6px; flex: 1">
          <div style="display: flex; align-items: center; gap: 12px; width: 100%">
            <label style="font-weight: 500; min-width: 90px; text-align: left">{{ $t('extractionFields.ai_method_label') }}</label>
            <span style="flex: 1; padding: 6px 12px; background: #f5f5f5; border: 1px solid #ccc; border-radius: 4px">
              {{ selectedAiMethod || $t('extractionFields.forms.placeholders.select_tenant') }}
            </span>
          </div>
          <div v-if="selectedValues.tenantId && userRole === 'Super Admin'" style="font-size: 13px; color: #8f969b; text-align: left; margin-left: 8px">
            {{ $t('extractionFields.configure_ai_method') }}
            <span @click="navigateToEdit({ id: selectedValues.tenantId })" style="color: var(--primary-color); text-decoration: underline; cursor: pointer"> {{ $t('extractionFields.tenant_details') }} </span>
          </div>
        </div>

        <!-- Search Bar -->
        <div class="searchBar" style="flex: 1; max-width: 300px; align-self: flex-start; margin-top: 0">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
            <circle cx="6.5" cy="7.45996" r="5.75" stroke="#8F969B" stroke-width="1.5" />
            <path d="M11 11.96L15 15.96" stroke="#8F969B" stroke-width="1.5" stroke-linecap="round" />
          </svg>
          <input type="text" class="searchInput" :placeholder="$t('extractionFields.search_placeholder')" v-model="search" @input="handleSearch" />
        </div>
      </div>

      <!-- Commented out Pagination Controls -->
      <!-- <div class="tableHeadingsNew" style="display: flex; align-items: center; margin-top: 20px; gap: 16px">
        <div class="customRow" style="display: flex; align-items: center; justify-content: start; gap: 16px; width: 100%">
          <img src="/backward.svg" alt="Back" style="cursor: pointer" @click="goToPreviousPage" />
          <span class="pageOfTitle">{{ $t('extractionFields.pagination.page') }} {{ page }} {{ $t('extractionFields.pagination.of') }} {{ totalPages }}</span>
          <img src="/forward.svg" alt="Next" style="cursor: pointer" @click="goToNextPage" />

          <div class="pageDiv" @click.stop="togglePageSizeDropdown">
            <span class="pageDivText">{{ pageSize }}</span>
            <img src="/arrowDownNew.png" class="arrowDownNew" alt="" />
            <ul ref="dropdownEl" v-if="showPageSizeDropdown" class="pageSizeDropdownNew" @click.stop>
              <li v-for="size in pageSizeOptions" :key="size" @click.stop="selectPageSize(size)">
                {{ size }}
              </li>
            </ul>
          </div>
        </div>
      </div> -->

      <!-- Extraction Fields Table -->
      <div class="tableContainer" style="margin-top: 20px; overflow-y: auto">
        <table class="customTable" style="font-size: 13px; overflow-y: auto">
          <thead class="customTableHeader">
            <tr class="customTableRow">
              <th style="font-size: 13px">{{ $t('extractionFields.table.headers.sr_no') }}</th>
              <th style="font-size: 13px">
                <div style="display: flex; align-items: center; gap: 6px">
                  <span>{{ $t('extractionFields.table.headers.vasion_field') }}</span>
                  <i class="bi bi-arrow-clockwise sync-icon-header" :title="$t('extractionFields.table.sync_tooltips.sync_vasion_fields')" @click="handleSyncNewFields"></i>
                </div>
              </th>
              <!-- <th style="width: 120px; font-size: 13px">{{ $t('extractionFields.table.headers.vasion_field_key') }}</th> -->
              <th style="width: 100px; font-size: 13px">{{ $t('extractionFields.table.headers.data_type') }}</th>
              <th style="font-size: 13px">
                <div style="display: flex; align-items: center; gap: 6px">
                  <span>{{ selectedAiMethod || $t('extractionFields.table.headers.ai_method_fields') }}</span>
                  <i class="bi bi-arrow-clockwise sync-icon-header" :title="$t('extractionFields.table.sync_tooltips.sync_ai_method_fields')" @click="handleSyncAiMethodFields"></i>
                </div>
              </th>
              <th style="width: 120px; font-size: 13px">{{ $t('extractionFields.table.headers.default_value_required') }}</th>
              <th style="font-size: 13px">{{ $t('extractionFields.table.headers.length') }}</th>
              <th style="font-size: 13px">{{ $t('extractionFields.table.headers.confidence_threshold') }}</th>
              <th style="font-size: 13px">{{ $t('extractionFields.table.headers.required_for_vasion') }}</th>
              <th style="font-size: 13px">{{ $t('extractionFields.table.headers.is_active') }}</th>
            </tr>
          </thead>

          <tbody class="customTableBody">
            <!-- Empty state message -->
            <tr v-if="!loading && extractionFields.length === 0" style="height: 80px">
              <td colspan="8" style="text-align: center; color: #8f969b; font-style: italic; font-size: 14px">
                {{ selectedValues.vasionFormId ? $t('extractionFields.noExtractionFieldsFound') : $t('extractionFields.pleaseSelectVasionForm') }}
              </td>
            </tr>

            <!-- Data rows -->
            <tr v-for="(fields, index) in extractionFields" :key="fields.id || index" style="height: 50px !important; font-size: 13px">
              <td style="font-size: 13px">{{ index + 1 + (page - 1) * pageSize }}</td>
              <td style="font-size: 13px">
                {{ fields.vasion_field_name || '-' }}
                <span v-if="isSystemField(fields.vasion_field_name)" style="color: #8f969b; font-style: italic; margin-left: 8px">
                  {{ $t('extractionFields.system_field') || 'System Field' }}
                </span>
              </td>
              <!-- <td style="font-size: 13px">
                <div class="vasion-field-key-container">
                  <span class="vasion-field-key-text" :class="{ 'vasion-field-key-truncated': fields?.vasion_field_key && fields.vasion_field_key.length > 20 }" @mouseenter="showTooltip($event, fields?.vasion_field_key)" @mouseleave="hideTooltip()">
                    {{ truncateText(fields?.vasion_field_key, 16) }}
                  </span>
                  <div v-if="tooltipVisible && tooltipText" class="vasion-field-key-tooltip" :style="tooltipStyle">
                    {{ tooltipText }}
                  </div>
                </div>
              </td> -->
              <td style="font-size: 13px">
                {{ fields.vasion_field_data_type || '-' }}
                <!-- {{ fields.vasion_field_data_type === 'Dropdown' ? ':' : '' }} -->

                <div v-if="fields.vasion_field_data_type === 'Dropdown' && fields.vasion_drop_down_values" class="dropdown-values-container">
                  <!-- <span class="dropdown-values-text" :class="{ 'dropdown-values-truncated': getFormattedDropdownValues(fields.vasion_drop_down_values).length > 25 }" @mouseenter="showDropdownTooltip($event, fields.vasion_drop_down_values)" @mouseleave="hideDropdownTooltip()">
                    {{ truncateText(getFormattedDropdownValues(fields.vasion_drop_down_values), 25) }}
                  </span> -->
                  <button class="dropdown-view-icon" @click="openDropdownModal(fields)" :title="$t('extractionFields.view_all_dropdown_values')">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M15 12C15 13.6569 13.6569 15 12 15C10.3431 15 9 13.6569 9 12C9 10.3431 10.3431 9 12 9C13.6569 9 15 10.3431 15 12Z" stroke="#7AC4EB" stroke-width="2" />
                      <path d="M2.458 12C3.732 7.943 7.523 5 12 5C16.478 5 20.268 7.943 21.542 12C20.268 16.057 16.478 19 12 19C7.523 19 3.732 16.057 2.458 12Z" stroke="#7AC4EB" stroke-width="2" />
                    </svg>
                  </button>
                  <div v-if="dropdownTooltipVisible && dropdownTooltipText" class="dropdown-values-tooltip" :style="dropdownTooltipStyle">
                    {{ dropdownTooltipText }}
                  </div>
                </div>
                <!-- <span v-else-if="fields.vasion_field_data_type === 'Dropdown'">-</span>
                <span v-else>-</span> -->
              </td>

              <!-- AI Method Field Selection Column -->
              <td v-if="this.selectedAiMethod == 'Custom Comprehend'" style="font-size: 13px">
                <span v-if="isSystemField(fields.vasion_field_name)" style="color: #8f969b; font-style: italic">
                  {{ $t('extractionFields.system_field') || 'System Field' }}
                </span>
                <select v-else :value="fields.document_field_id || ''" @change="(event) => handleCustomComprehendFieldChange(event, fields)" class="tenant-input" style="font-size: 13px">
                  <!-- CRITICAL FIX: Use empty string for default to show 'Select Field' properly -->
                  <option value="">{{ $t('extractionFields.forms.placeholders.select_field') }}</option>
                  <option v-for="option in aiMethodFieldsMap[selectedAiMethod] || []" :key="option.id" :value="option.id">{{ option.field_label }}</option>
                </select>
              </td>
              <td v-else-if="this.selectedAiMethod === 'Bedrock Data Automation'" style="font-size: 13px">
                <span v-if="isSystemField(fields.vasion_field_name)" style="color: #8f969b; font-style: italic">
                  {{ $t('extractionFields.system_field') || 'System Field' }}
                </span>
                <select v-else :value="fields.selected_bedrock_field || ''" @change="(event) => handleBedrockFieldChange(event, fields)" class="tenant-input" style="font-size: 13px">
                  <!-- CRITICAL FIX: Use empty string for default to show 'Select Field' properly -->
                  <option value="">{{ $t('extractionFields.forms.placeholders.select_field') }}</option>
                  <option v-if="isDocumentSummaryRequired" value="document_summary">document_summary</option>
                  <option v-for="option in getAiMethodFields()" :key="option" :value="option">{{ option }}</option>
                </select>
              </td>
              <td v-else style="font-size: 13px">
                <span v-if="isSystemField(fields.vasion_field_name)" style="color: #8f969b; font-style: italic">
                  {{ $t('extractionFields.system_field') || 'System Field' }}
                </span>
                <select v-else :value="fields.selected_vasion_field_key || ''" @change="(event) => handleOtherAiFieldChange(event, fields)" class="tenant-input" style="font-size: 13px">
                  <!-- CRITICAL FIX: Use empty string for default to show 'Select Field' properly -->
                  <option value="">{{ $t('extractionFields.forms.placeholders.select_field') }}</option>
                  <option v-for="option in getAiMethodFields()" :key="option" :value="option">{{ option }}</option>
                </select>
              </td>
              <td style="font-size: 13px; text-align: center">
                <input v-if="fields.vasion_field_data_type === 'Dropdown' && fields.vasion_drop_down_values" type="checkbox" :checked="fields.default_value_required" style="opacity: 0.8; font-size: 13px" @click.prevent="(event) => handleDefaultCheck(fields, !fields.default_value_required)" />
                <span v-else>-</span>
              </td>
              <td style="font-size: 13px">{{ fields.vasion_field_length || '-' }}</td>
              <td style="font-size: 13px">
                <input type="number" step="0.01" min="0" max="100" :value="fields.ml_confidence_level || 0" @change="handleConfidenceChange($event, fields)" class="confidence-input" pattern="[0-9]*\.?[0-9]*" style="font-size: 13px" />
              </td>
              <td style="font-size: 13px">
                <input type="checkbox" :checked="fields.vasion_required || false" style="cursor: not-allowed; opacity: 0.8; font-size: 13px" @click.prevent />
              </td>
              <td style="font-size: 13px">
                <div class="toggle-wrapper">
                  <CustomToggle :modelValue="Boolean(fields.is_active)" @update:modelValue="(value) => handleStatusToggle(fields, value)" />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <ConfirmationModal :show="showConfirmModal" :message="$t('extractionFields.modals.confirm_status.message', { fieldName: pendingStatusChange?.field?.vasion_field_name })" @close="closeConfirmModal" @confirm="confirmStatusChange" />
    <ConfirmationModal :show="showConfirmModalForDefault" :message="$t('extractionFields.modals.confirm_default_value.message', { fieldName: pendingStatusChange?.field?.vasion_field_name })" @close="closeConfirmModal" @confirm="confirmDefaultValueChange" />
    <DropdownValuesModal :show="showDropdownModal" :dropdownValues="selectedDropdownValues" :fieldName="selectedFieldName" @close="closeDropdownModal" />
  </div>
  <div v-else>
    <PermissionAlert />
  </div>
</template>

<script>
import { get, post, put } from '@/utils/api'
import CustomToggle from '@/components/CustomToggle/CustomToggle.vue'
import { showToast } from '@/utils/toast'
import ConfirmationModal from '@/components/ConfirmationModal/ConfirmationModal.vue'
import DropdownValuesModal from '@/components/DropdownValuesModal/DropdownValuesModal.vue'
import PermissionAlert from '@/components/PermissionAlert.vue'
export default {
  emits: ['complete'],
  props: {
    // Props for step mode integration
    isStepMode: {
      type: Boolean,
      default: false,
    },
    tenantId: {
      type: [String, Number],
      default: null,
    },
    documentTypeId: {
      type: [String, Number],
      default: null,
    },
    documentTypeName: {
      type: String,
      default: 'New Document Type',
    },
    sourceDocumentName: {
      type: String,
      default: '',
    },
    blueprintArn: {
      type: String,
      default: '',
    },
    blueprintSchema: {
      type: Object,
      default: () => ({}),
    },
    selectedTenantUsesBedrock: {
      type: Boolean,
      default: false,
    },
    selectedTenantUsesTextract: {
      type: Boolean,
      default: false,
    },
    presetVasionFormId: {
      type: [String, Number],
      default: null,
    },
    presetFieldMappings: {
      type: Array,
      default: () => [],
    },
  },
  components: {
    CustomToggle,
    ConfirmationModal,
    DropdownValuesModal,
    PermissionAlert,
  },
  computed: {
    loading() {
      return Object.values(this.loadingStates).some((state) => state)
    },
    // Get blueprint schema fields for Bedrock Data Automation dropdown
    blueprintFields() {
      if (this.blueprintSchema && this.blueprintSchema.properties) {
        const fields = Object.keys(this.blueprintSchema.properties)
        return fields
      }
      return []
    },
  },
  data() {
    return {
      userRole: localStorage.getItem('userRole'),
      readPermissionMaster: false,
      writePermissionMaster: false,
      readPermission: false,
      writePermission: false,
      isDocumentSummaryRequired: false,
      loadingStates: {
        tenants: false,
        updateExtractionField: false,
        departments: false,
        vasionForms: false,
        tenantCategories: false,
        documentTypes: false,
        extractionFields: false,
        syncVasionFields: false,
      },
      aiMethodFieldsMap: {
        'Default Textract': [],
        'Custom Comprehend': [],
        'Bedrock Data Automation': [],
      },
      selectedAiMethod: '', // You can bind this from tenant config if needed
      departments: [],
      categories: [], // To store categories for dropdown
      tenants: [],
      filters: null,
      documentTypes: [],
      vasionForms: [],
      search: '',
      debounceTimeout: null,
      extractionFields: [],
      page: 1,
      pageSize: 10,
      total: 0,
      totalPages: 1,
      pageSizeOptions: [10, 20, 50, 100],
      showPageSizeDropdown: false,
      newDepartment: {
        categoryId: '',
        name: '',
        isActive: true,
      },
      selectedValues: {
        tenantId: '',
        categoryId: '',
        departmentId: '',
        documentTypeId: '',
        vasionFormId: '',
      },
      selectedDocType: null,
      showConfirmModal: false,
      showConfirmModalForDefault: false,
      pendingStatusChange: null,
      // Tooltip data for vasion field key
      tooltipVisible: false,
      tooltipText: '',
      tooltipStyle: {},
      // Tooltip data for dropdown values
      dropdownTooltipVisible: false,
      dropdownTooltipText: '',
      dropdownTooltipStyle: {},
      // Dropdown modal data
      showDropdownModal: false,
      selectedDropdownValues: [],
      selectedFieldName: '',
      // Add extraction mapping list for Bedrock flow
      extractionMappingList: [],
      // Flag to track if user has made changes to avoid overwriting
      hasUserMadeChanges: false,
      // Flag to track component initialization state
      isInitialLoad: true,
      // Flag to track if fields have been loaded to prevent unnecessary overwrites
      fieldsLoaded: false,
      // Editable document type name
      editableDocumentTypeName: '',
    }
  },
  mounted() {
    document.title = this.$t('extractionFields.title')
    this.checkPermissions(this.tenantId)
    this.fetchAndSetTenantAiMethod()

    // Initialize editable document type name
    this.editableDocumentTypeName = this.getDocumentTypeName('doctype')

    // Always fetch Vasion forms if we have a tenantId
    if (this.tenantId) {
      this.fetchVasionForms(this.tenantId)
    }

    // If in step mode, pre-populate with props
    if (this.isStepMode && this.tenantId && this.documentTypeId) {
      this.selectedValues.tenantId = this.tenantId
      this.selectedValues.documentTypeId = this.documentTypeId
      this.fetchExtractionFields()
    } else {
      this.fetchTenants()
      this.fetchExtractionFields()
    }

    document.addEventListener('click', this.handleClickAway)

    // After initial mount, mark as no longer initial load after a short delay
    this.$nextTick(() => {
      setTimeout(() => {
        this.isInitialLoad = false
      }, 1000) // 1 second should be enough for initial data loading
    })
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickAway)
  },
  methods: {
    checkPermissions() {
      const permissions = JSON.parse(localStorage.getItem('permissions') || '[]')

      this.readPermissionMaster = permissions.some((p) => p.object_name === 'MASTER_CONFIGURE_RD' && p.is_active)
      this.writePermissionMaster = permissions.some((p) => p.object_name === 'MASTER_CONFIGURE_WR' && p.is_active)
      this.readPermission = permissions.some((p) => p.object_name === 'TENANT_CONFIGURE_RD' && p.is_active)
      this.writePermission = permissions.some((p) => p.object_name === 'TENANT_CONFIGURE_WR' && p.is_active)
    },
    isSystemField(fieldName) {
      const systemFields = ['Status', 'Document Type', 'Index Date']
      return systemFields.includes(fieldName)
    },
    handleClickAway(event) {
      const dropdownEl = this.$refs.dropdownEl
      if (dropdownEl && !dropdownEl.contains(event.target)) {
        this.showPageSizeDropdown = false
        this.showEditCategoryDropdown = false
      }

      // Close tooltips when clicking outside
      this.hideTooltip()
      this.hideDropdownTooltip()
    },
    navigateToEdit(tenant) {
      const tenantName = localStorage?.getItem('url_name') || ''
      this.$router.push(`/${tenantName}/tenant/edit/${tenant.id}`)
    },
    async fetchTenants() {
      this.loadingStates.tenants = true
      try {
        const res = await get('/v1/tenants?page=1&pageSize=1000')
        if (res?.data?.code === 200) {
          this.tenants = (res.data.data.tenants || [])
            .filter((tenant) => tenant.is_active)
            .map((tenant) => ({
              label: tenant.tenant_name,
              value: tenant.id,
            }))
        }
      } catch (err) {
        // Error fetching tenants
      } finally {
        this.loadingStates.tenants = false
      }
    },
    async fetchAndSetTenantAiMethod(tenantId) {
      try {
        const response = await get(`/v1/tenant-configs/tenant/${this.tenantId}`)
        if (response?.data?.code === 200) {
          const config = response.data.data
          let aiMethod = config.find((item) => item.key === 'ai_method')?.value
          const doc_summary = config.find((item) => item.key === 'document_summary')?.value
          if (doc_summary === 'true') {
            this.isDocumentSummaryRequired = true
          } else {
            this.isDocumentSummaryRequired = false
          }
          // Handle variations of AI method names
          if (aiMethod === 'Custom Comprehend - Async' || aiMethod === 'Custom Comprehend - Realtime') {
            aiMethod = 'Custom Comprehend'
          }

          this.selectedAiMethod = aiMethod || ''
          console.log('🤖 AI Method set to:', this.selectedAiMethod)
        }
      } catch (err) {
        console.error('Failed to fetch tenant AI method from config:', err)
      }
    },

    async fetchTenantCategories(tenantId) {
      this.loadingStates.tenantCategories = true
      try {
        const response = await get(`/v1/tenant-categories?tenantId=${tenantId}&page=1&pageSize=100000&isActive=true`)
        if (response?.data?.code === 200) {
          this.categories = (response.data.data.data || [])
            .filter((category) => category.is_active)
            .map((category) => ({
              id: category.category_id,
              category_name: category.category_display_name,
            }))
        }
      } catch (err) {
        console.error('Failed to fetch tenant categories', err)
      } finally {
        this.loadingStates.tenantCategories = false
      }
    },
    async fetchTenantDepartments(tenantId, categoryId) {
      this.loadingStates.departments = true
      try {
        const url = `/v1/tenant-departments?tenantId=${tenantId}&categoryId=${categoryId}&page=1&pageSize=100000`
        const response = await get(url)
        if (response?.data?.code === 200) {
          this.departments = (response.data.data.data || [])
            .filter((dept) => dept.is_active)
            .map((dept) => ({
              id: dept.department_id,
              department_name: dept.department_display_name,
            }))
        }
      } catch (err) {
        console.error('Failed to fetch tenant departments', err)
        this.departments = []
      } finally {
        this.loadingStates.departments = false
      }
    },
    async fetchTenantDocumentTypes(tenantId, categoryId, departmentId) {
      this.loadingStates.documentTypes = true
      try {
        const url = `/v1/tenant-document-types?page=1&pageSize=10000&categoryId=${categoryId}&departmentId=${departmentId}&tenantId=${tenantId}`
        //  const url = `/v1/document-types?page=1&pageSize=10000&category_id=${categoryId}&department_id=${departmentId}`
        const response = await get(url)
        if (response?.data?.code === 200) {
          this.documentTypes = (response.data.data.data || [])
            .filter((doc) => doc.is_active)
            .map((doc) => ({
              id: doc.id,
              document_type_name: doc.document_type_display_name,
              vasion_form_id: doc.vasion_form_id,
              master_document_type_id: doc.document_type_id,
              model_id: doc.document_type.model_id,
              bda_blueprint: doc.bda_blueprint,
              bedrock_class_name: doc.bedrock_class_name,
            }))
        }
      } catch (err) {
        console.error('Failed to fetch tenant document types', err)
      } finally {
        this.loadingStates.documentTypes = false
      }
    },
    getAiMethodFields() {
      // For Bedrock Data Automation and Textract, use blueprint schema fields if available
      if (this.selectedAiMethod === 'Bedrock Data Automation' || this.selectedAiMethod === 'Textract') {
        if (this.blueprintFields.length > 0) {
          return this.blueprintFields
        }
      }
      // Fallback to existing aiMethodFieldsMap for other AI methods
      return this.aiMethodFieldsMap[this.selectedAiMethod] || []
    },
    getComprehendFields(selectedDocType) {
      if (!selectedDocType.master_document_type_id || this.selectedAiMethod !== 'Default Textract') return
      this.aiMethodFieldsMap[this.selectedAiMethod] = [
        { id: 1, field_label: 'ABC', data_type: 'string' },
        { id: 2, field_label: 'EFG', data_type: 'date' },
      ]
    },

    async handleSyncAiMethodFields() {
      if (!this.selectedAiMethod || !this.selectedValues.documentTypeId) return

      try {
        let res
        let fields = []

        switch (this.selectedAiMethod) {
          case 'Default Textract':
            break

          case 'Custom Comprehend':
            res = await get(`/v1/documentFields/${this.selectedDocType.master_document_type_id}/${this.selectedDocType.model_id}`)
            fields = res.data.data
            break

          case 'Bedrock Data Automation':
            res = await post(`/v1/bda/getBluePrintDetails`, {
              blueprintArn: this.documentTypes?.find((doc) => doc.id === this.selectedDocType.id)?.bda_blueprint,
            })
            if (res?.data?.code === 201 && res.data.data && res.data.data.schema) {
              // Parse the schema JSON string
              const schema = JSON.parse(res.data.data.schema)

              // Extract property keys from the schema
              if (schema.properties) {
                const propertyKeys = Object.keys(schema.properties)

                // Convert property keys to the expected format
                fields = propertyKeys
              }
            }
            break

          default:
            showToast({
              title: this.$t('common.error'),
              message: this.$t('extractionFields.messages.error.unsupported_ai_method'),
              type: 'error',
            })
            return
        }

        // Store result
        this.aiMethodFieldsMap[this.selectedAiMethod] = fields
        showToast({
          title: this.$t('common.success'),
          message: this.$t('extractionFields.messages.success.ai_method_fields_synced'),
          type: 'success',
        })
      } catch (err) {
        showToast({
          title: this.$t('common.error'),
          message: this.$t('extractionFields.messages.error.failed_to_fetch_ai_fields'),
          type: 'error',
        })
      }
    },

    async fetchVasionForms(tenantId) {
      this.loadingStates.vasionForms = true
      try {
        const response = await get(`/v1/vasion/objects?tenantId=${tenantId}`)
        if (response?.data?.code === 200) {
          // this.vasionForms = response.data.data || []
          if (response.data.data && response.data.data.IndexForms) {
            this.vasionForms = Object.entries(response.data.data.IndexForms || []).map(([key, value]) => ({
              id: parseInt(key),
              vasion_form_id: value,
            }))
          } else {
            this.vasionForms = []
          }
        }
      } catch (err) {
        console.error('Failed to fetch vasion forms', err)
      } finally {
        this.loadingStates.vasionForms = false
      }
    },
    handleVasionFormChange() {
      // Mark that user has made changes
      this.hasUserMadeChanges = true
      // The watcher for selectedValues.vasionFormId will handle fetchExtractionFields call
    },
    // New method specifically for handling Bedrock field changes
    async handleBedrockFieldChange(event, field) {
      if (!this.writePermissionMaster && !this.writePermission) {
        showToast({
          title: this.$t('common.error'),
          message: this.$t('extractionFields.messages.error.no_permission_update_fields'),
          type: 'error',
        })
        return
      }

      // Mark that user has made changes
      this.hasUserMadeChanges = true

      const selectedValue = event.target.value
      console.log('Bedrock field change:', {
        fieldName: field.vasion_field_name,
        fieldId: field.id,
        selectedValue: selectedValue,
        isEmpty: selectedValue === '',
      })

      // CRITICAL FIX: Handle empty string as deselection (null), non-empty as selection
      const normalizedValue = selectedValue === '' ? null : selectedValue

      // CRITICAL FIX: Update the field object directly for Vue reactivity
      field.selected_bedrock_field = normalizedValue
      field.bedrock_field = normalizedValue
      field.ai_method_field = normalizedValue

      // Force Vue reactivity update
      this.$forceUpdate()

      console.log('Updated field:', {
        fieldName: field.vasion_field_name,
        selected_bedrock_field: field.selected_bedrock_field,
        normalizedValue: normalizedValue,
      })

      const updatedField = {
        ...field,
        ai_method_field: normalizedValue,
        selected_bedrock_field: normalizedValue,
        bedrock_field: normalizedValue,
      }

      // If using Bedrock or Textract in step mode, update local list instead of calling API
      if ((this.selectedTenantUsesBedrock || this.selectedTenantUsesTextract) && this.isStepMode) {
        console.log('🔄 Updating extractionMappingList for Bedrock/Textract field:', {
          fieldId: field.id,
          fieldName: field.vasion_field_name,
          newValue: normalizedValue,
          listCountBefore: this.extractionMappingList?.length || 0,
        })
        this.updateExtractionMappingList(field.id, updatedField)
        console.log('✅ extractionMappingList updated - count after:', this.extractionMappingList?.length || 0)
      } else {
        await this.handleExtractionFieldUpdate(field.id, updatedField, 'aiMethod')
      }
    },
    // New method specifically for handling Custom Comprehend field changes
    async handleCustomComprehendFieldChange(event, field) {
      if (!this.writePermissionMaster && !this.writePermission) {
        showToast({
          title: this.$t('common.error'),
          message: this.$t('extractionFields.messages.error.no_permission_update_fields'),
          type: 'error',
        })
        return
      }

      // Mark that user has made changes
      this.hasUserMadeChanges = true

      const selectedValue = event.target.value
      console.log('Custom Comprehend field change:', {
        fieldName: field.vasion_field_name,
        fieldId: field.id,
        selectedValue: selectedValue,
        isEmpty: selectedValue === '',
      })

      // CRITICAL FIX: Handle empty string as deselection (null), non-empty as selection
      // For Custom Comprehend, we need to handle number conversion for document_field_id
      const normalizedValue = selectedValue === '' ? null : parseInt(selectedValue, 10) || null

      // CRITICAL FIX: Update the field object directly for Vue reactivity
      field.document_field_id = normalizedValue
      field.ai_method_field = normalizedValue

      // Force Vue reactivity update
      this.$forceUpdate()

      console.log('Updated field:', {
        fieldName: field.vasion_field_name,
        document_field_id: field.document_field_id,
        normalizedValue: normalizedValue,
      })

      const updatedField = {
        ...field,
        document_field_id: normalizedValue,
        ai_method_field: normalizedValue,
      }

      // If using Bedrock or Textract in step mode, update local list instead of calling API
      if ((this.selectedTenantUsesBedrock || this.selectedTenantUsesTextract) && this.isStepMode) {
        this.updateExtractionMappingList(field.id, updatedField)
      } else {
        await this.handleExtractionFieldUpdate(field.id, updatedField, 'aiMethod')
      }
    },
    // New method specifically for handling other AI method field changes
    async handleOtherAiFieldChange(event, field) {
      if (!this.writePermissionMaster && !this.writePermission) {
        showToast({
          title: this.$t('common.error'),
          message: this.$t('extractionFields.messages.error.no_permission_update_fields'),
          type: 'error',
        })
        return
      }

      // Mark that user has made changes
      this.hasUserMadeChanges = true

      const selectedValue = event.target.value
      console.log('Other AI field change:', {
        fieldName: field.vasion_field_name,
        fieldId: field.id,
        selectedValue: selectedValue,
        isEmpty: selectedValue === '',
      })

      // CRITICAL FIX: Handle empty string as deselection (null), non-empty as selection
      const normalizedValue = selectedValue === '' ? null : selectedValue

      // CRITICAL FIX: Update the field object directly for Vue reactivity
      // NEVER modify vasion_field_key - it must always remain as the original Vasion API field name
      field.selected_vasion_field_key = normalizedValue
      // CRITICAL: For Textract, also set bedrock_field and ai_method_field for backend compatibility
      field.bedrock_field = normalizedValue || ''
      field.ai_method_field = normalizedValue || ''

      // Force Vue reactivity update
      this.$forceUpdate()

      console.log('Updated field:', {
        fieldName: field.vasion_field_name,
        selected_vasion_field_key: field.selected_vasion_field_key,
        bedrock_field: field.bedrock_field,
        ai_method_field: field.ai_method_field,
        normalizedValue: normalizedValue,
        vasion_field_key: field.vasion_field_key, // Should remain unchanged
      })

      const updatedField = {
        ...field,
        ai_method_field: normalizedValue || '',
        bedrock_field: normalizedValue || '',
        selected_vasion_field_key: normalizedValue,
        // NEVER include vasion_field_key in updates - it's immutable
      }

      // If using Bedrock or Textract in step mode, update local list instead of calling API
      if ((this.selectedTenantUsesBedrock || this.selectedTenantUsesTextract) && this.isStepMode) {
        this.updateExtractionMappingList(field.id, updatedField)
      } else {
        await this.handleExtractionFieldUpdate(field.id, updatedField, 'aiMethod')
      }
    },
    async handleAiFieldSelect(field) {
      if (!this.writePermissionMaster && !this.writePermission) {
        showToast({
          title: this.$t('common.error'),
          message: this.$t('extractionFields.messages.error.no_permission_update_fields'),
          type: 'error',
        })
        return
      }

      // Mark that user has made changes
      this.hasUserMadeChanges = true

      // For Bedrock Data Automation, update the field data
      const selectedValue = this.selectedAiMethod === 'Bedrock Data Automation' ? field.selected_bedrock_field : field.selected_vasion_field_key

      // CRITICAL FIX: Properly handle null/empty values for deselection
      const normalizedValue = selectedValue === '' || selectedValue === null || selectedValue === undefined ? null : selectedValue

      // CRITICAL FIX: Directly update the field object for Vue 3 reactivity
      // NEVER modify vasion_field_key - it must always remain as the original Vasion API field name
      if (this.selectedAiMethod === 'Bedrock Data Automation') {
        // Update Bedrock field selection
        field.selected_bedrock_field = normalizedValue
        field.bedrock_field = normalizedValue
      } else {
        // Update other AI method field selection (Textract, etc.)
        field.selected_vasion_field_key = normalizedValue
        // DO NOT SET field.vasion_field_key - it's immutable!
      }
      field.ai_method_field = normalizedValue

      // Find and update the field in the extractionFields array to trigger reactivity
      const fieldIndex = this.extractionFields.findIndex((f) => f.id === field.id)
      if (fieldIndex !== -1) {
        this.extractionFields[fieldIndex] = { ...field }
      }

      const updatedField = {
        ...field,
        ai_method_field: normalizedValue,
        // Update the correct field based on AI method
        ...(this.selectedAiMethod === 'Bedrock Data Automation'
          ? {
              selected_bedrock_field: normalizedValue,
              bedrock_field: normalizedValue,
            }
          : {
              selected_vasion_field_key: normalizedValue,
              // NEVER include vasion_field_key - it's immutable!
            }),
      }

      // If using Bedrock or Textract in step mode, update local list instead of calling API
      if ((this.selectedTenantUsesBedrock || this.selectedTenantUsesTextract) && this.isStepMode) {
        this.updateExtractionMappingList(field.id, updatedField)
      } else {
        await this.handleExtractionFieldUpdate(field.id, updatedField, 'aiMethod')
      }
    },
    async handleDocumentFieldSelect(field) {
      if (!this.writePermissionMaster && !this.writePermission) {
        showToast({
          title: this.$t('common.error'),
          message: this.$t('extractionFields.messages.error.no_permission_update_fields'),
          type: 'error',
        })
        return
      }

      try {
        const payload = field
        await this.handleExtractionFieldUpdate(field.id, payload, 'aiMethod')
      } catch (error) {
        console.error('Field update failed:', error)
        showToast({
          title: this.$t('extractionFields.messages.error.update_failed'),
          message: this.$t('extractionFields.messages.error.field_update_error'),
          type: 'error',
        })
      }
    },

    async fetchExtractionFields() {
      // For step mode, we need at least tenantId and vasionFormId
      // For regular mode, we need the full hierarchy
      const hasRequiredForStepMode = this.isStepMode && this.tenantId && this.selectedValues.vasionFormId
      const hasRequiredForRegularMode = !this.isStepMode && this.selectedValues.tenantId && this.selectedValues.categoryId && this.selectedValues.departmentId && this.selectedValues.documentTypeId

      if (hasRequiredForStepMode || hasRequiredForRegularMode) {
        this.loadingStates.extractionFields = true
        try {
          // CRITICAL FIX: Use proper IDs for both step mode and regular mode
          const effectiveTenantId = this.isStepMode ? this.tenantId : this.selectedValues.tenantId
          const effectiveDocumentTypeId = this.isStepMode ? this.documentTypeId : this.selectedValues.documentTypeId

          const params = {
            page: this.page,
            pageSize: this.pageSize,
            ...(effectiveTenantId && {
              tenantId: effectiveTenantId,
            }),
            ...(this.selectedValues.categoryId && {
              categoryId: this.selectedValues.categoryId,
            }),
            ...(this.selectedValues.departmentId && {
              departmentId: this.selectedValues.departmentId,
            }),
            // CRITICAL: Send tenantDocumentTypeId for proper field merging
            ...(effectiveDocumentTypeId && {
              tenantDocumentTypeId: effectiveDocumentTypeId,
            }),
            ...(this.selectedValues.vasionFormId && {
              vasionFormId: this.selectedValues.vasionFormId,
            }),
            ...(this.search && {
              vasionFieldName: this.search,
            }),
          }

          // Add filter params if filters exist
          if (this.filters) {
            Object.entries(this.filters).forEach(([key, value]) => {
              if (value !== '' && value !== null && value !== undefined) {
                // Convert boolean values to strings if needed
                params[key] = typeof value === 'boolean' ? value.toString() : value
              }
            })
          }

          const queryString = new URLSearchParams(params).toString()
          const url = `/v1/tenant-document-type-fields/bda-vasion-fields?${queryString}`

          const response = await post(url)

          if (response?.data?.code === 200) {
            const responseData = response.data.data

            // Handle the specific API structure: response.data.data.Fields.Values
            if (responseData?.Fields?.Values && Array.isArray(responseData.Fields.Values)) {
              // IMPORTANT: Preserve original Vasion field structure to maintain integration
              // Only add UI-specific properties without modifying core Vasion data
              console.log('=== FIELD PRIORITY RESOLUTION DEBUG ===')
              console.log('extractionMappingList count:', this.extractionMappingList?.length || 0)
              console.log('presetFieldMappings count:', this.presetFieldMappings?.length || 0)
              console.log('existingFields count:', this.extractionFields?.length || 0)

              this.extractionFields = responseData.Fields.Values.map((field, index) => {
                // CRITICAL FIX: Check if we have existing user selections for this field
                const existingMapping = this.extractionMappingList.find((mapping) => mapping.id === field.FieldID)
                // CRITICAL FIX: Only use existingField if we don't have local changes
                // This prevents database values from overriding user selections when navigating back
                const existingField = !existingMapping ? this.extractionFields.find((existing) => existing.id === field.FieldID) : null
                const presetMapping = this.presetFieldMappings?.find((preset) => preset.vasion_field_id === field.FieldID || preset.vasion_field_key === field.FieldName || preset.id === field.FieldID)

                // Debug log for each field to see what sources are available
                if (existingMapping || presetMapping || existingField) {
                  console.log(`Field '${field.DisplayName}' sources:`, {
                    hasLocalMapping: !!existingMapping,
                    hasPresetMapping: !!presetMapping,
                    hasExistingField: !!existingField,
                    localBedrock: existingMapping?.selected_bedrock_field,
                    presetBedrock: presetMapping?.selected_bedrock_field || presetMapping?.bedrock_field,
                    existingBedrock: existingField?.selected_bedrock_field,
                    PRIORITY_USED: existingMapping ? 'LOCAL' : existingField ? 'DATABASE' : presetMapping ? 'PRESET' : 'DEFAULT',
                  })
                }

                return {
                  // Preserve original Vasion identifiers and structure
                  id: field.FieldID,
                  vasion_field_name: field.DisplayName,
                  // CRITICAL: ALWAYS use field.FieldName from Vasion API - NEVER trust preset/existing data
                  // This is the immutable Vasion field key that must never change
                  vasion_field_key: field.FieldName,
                  vasion_field_data_type: this.getFieldDataType(field.Control),
                  vasion_field_length: field.FieldLength,
                  vasion_required: field.Required,
                  vasion_drop_down_values: field.DropDownValues ? JSON.stringify(field.DropDownValues.Values) : null,

                  // UI-specific properties - priority: user changes > existing > preset > API default
                  is_active: (() => {
                    const isActiveValue = existingMapping?.is_active !== undefined ? existingMapping.is_active : existingField?.is_active !== undefined ? existingField.is_active : presetMapping?.is_active !== undefined ? presetMapping.is_active : field.IsActive !== undefined ? field.IsActive : true
                    return Boolean(isActiveValue)
                  })(),

                  ml_confidence_level: existingMapping?.ml_confidence_level !== undefined ? existingMapping.ml_confidence_level : existingField?.ml_confidence_level !== undefined ? existingField.ml_confidence_level : presetMapping?.ml_confidence_level !== undefined ? presetMapping.ml_confidence_level : 90,

                  default_value_required: existingMapping?.default_value_required !== undefined ? existingMapping.default_value_required : existingField?.default_value_required !== undefined ? existingField.default_value_required : presetMapping?.default_value_required !== undefined ? presetMapping.default_value_required : field.DefaultValueRequired !== undefined ? field.DefaultValueRequired : false,

                  // CRITICAL FIX: Prioritize local user changes for document field selection
                  document_field_id: (() => {
                    if (existingMapping && 'document_field_id' in existingMapping) {
                      console.log(`🔄 Using LOCAL user document field for '${field.DisplayName}':`, existingMapping.document_field_id)
                      return existingMapping.document_field_id
                    } else if (presetMapping && 'document_field_id' in presetMapping) {
                      console.log(`📋 Using PRESET document field for '${field.DisplayName}':`, presetMapping.document_field_id)
                      return presetMapping.document_field_id
                    } else if (existingField && 'document_field_id' in existingField) {
                      console.log(`🗄️  Using DATABASE document field for '${field.DisplayName}':`, existingField.document_field_id)
                      return existingField.document_field_id
                    }
                    console.log(`🆕 Using DEFAULT (null) document field for '${field.DisplayName}'`)
                    return null
                  })(),

                  selected_bedrock_field: (() => {
                    // CRITICAL FIX: Prioritize local user changes (extractionMappingList) over everything
                    // This ensures user selections persist when navigating back from next steps
                    if (existingMapping && 'selected_bedrock_field' in existingMapping) {
                      console.log(`🔄 Using LOCAL user selection for '${field.DisplayName}':`, existingMapping.selected_bedrock_field)
                      return existingMapping.selected_bedrock_field
                    } else if (presetMapping && 'selected_bedrock_field' in presetMapping) {
                      console.log(`📋 Using PRESET mapping for '${field.DisplayName}':`, presetMapping.selected_bedrock_field)
                      return presetMapping.selected_bedrock_field
                    } else if (presetMapping && 'bedrock_field' in presetMapping) {
                      console.log(`📋 Using PRESET bedrock_field for '${field.DisplayName}':`, presetMapping.bedrock_field)
                      return presetMapping.bedrock_field
                    } else if (existingField && 'selected_bedrock_field' in existingField) {
                      console.log(`🗄️  Using DATABASE value for '${field.DisplayName}':`, existingField.selected_bedrock_field)
                      return existingField.selected_bedrock_field
                    }
                    console.log(`🆕 Using DEFAULT (null) for '${field.DisplayName}'`)
                    return null
                  })(),

                  selected_vasion_field_key: (() => {
                    // CRITICAL FIX: Prioritize local user changes (extractionMappingList) over everything
                    // This ensures user selections persist when navigating back from next steps
                    if (existingMapping && 'selected_vasion_field_key' in existingMapping) {
                      console.log(`🔄 Using LOCAL user vasion selection for '${field.DisplayName}':`, existingMapping.selected_vasion_field_key)
                      return existingMapping.selected_vasion_field_key
                    } else if (presetMapping && 'selected_vasion_field_key' in presetMapping) {
                      console.log(`📋 Using PRESET vasion selection for '${field.DisplayName}':`, presetMapping.selected_vasion_field_key)
                      return presetMapping.selected_vasion_field_key
                    } else if (presetMapping && 'bedrock_field' in presetMapping && presetMapping.bedrock_field) {
                      // TEXTRACT EDIT FIX: For Textract, bedrock_field contains the Textract value
                      console.log(`📋 Using PRESET bedrock_field as vasion selection for '${field.DisplayName}':`, presetMapping.bedrock_field)
                      return presetMapping.bedrock_field
                    } else if (existingField && 'selected_vasion_field_key' in existingField) {
                      console.log(`🗄️  Using DATABASE vasion selection for '${field.DisplayName}':`, existingField.selected_vasion_field_key)
                      return existingField.selected_vasion_field_key
                    }
                    console.log(`🆕 Using DEFAULT (null) vasion selection for '${field.DisplayName}'`)
                    return null
                  })(),

                  // Preserve complete original field data for backend integration
                  _original: field,

                  // Additional Vasion properties that might be needed
                  control: field.Control,
                  field_order: field.FieldOrder,
                }
              })

              this.total = this.extractionFields.length
              this.totalPages = Math.ceil(this.total / this.pageSize)

              // DISABLED: This logic was potentially overwriting local user changes
              // Local changes should already be preserved via the priority logic above
              // this.extractionFields.forEach((field) => {
              //   if (field.selected_bedrock_field || field.document_field_id || field.ml_confidence_level !== 90 || field.is_active !== true || field.default_value_required !== false) {
              //     // Field has been modified from defaults, ensure it's in the mapping list
              //     this.updateExtractionMappingList(field.id, field)
              //   }
              // })
              console.log('🗺 Final extractionMappingList count after field processing:', this.extractionMappingList?.length || 0)

              // Mark fields as loaded
              this.fieldsLoaded = true

              // DISABLED: Preset field mappings are now applied via the presetFieldMappings watcher
              // This prevents double-application that could interfere with restored user selections
              // this.applyPresetFieldMappings()
              console.log('🔚 BDAExtraction: Skipping applyPresetFieldMappings (handled by watcher)')
            } else {
              this.extractionFields = []
              this.total = 0
              this.totalPages = 1
            }
          } else {
            this.extractionFields = []
            this.total = 0
            this.totalPages = 1
          }
        } catch (error) {
          console.error('Failed to fetch extraction fields:', error)
          showToast({
            title: this.$t('common.error'),
            message: 'Failed to fetch extraction fields: ' + (error.message || 'Unknown error'),
            type: 'error',
          })
          this.extractionFields = []
          this.total = 0
          this.totalPages = 1
        } finally {
          this.loadingStates.extractionFields = false
        }
      } else {
        this.extractionFields = []
        this.total = 0
        this.totalPages = 1
      }
    },
    async handleFieldChange(fieldName, newValue) {
      if (fieldName === 'tenantId') {
        // Reset dependent fields
        this.selectedValues = {
          ...this.selectedValues,
          categoryId: '',
          departmentId: '',
          documentTypeId: '',
          vasionFormId: '',
        }
        this.categories = []
        this.departments = []
        this.documentTypes = []
        this.vasionForms = []
        this.selectedAiMethod = ''

        if (newValue) {
          await Promise.all([this.fetchTenantCategories(newValue), this.fetchVasionForms(newValue), this.fetchAndSetTenantAiMethod(newValue)])
        }
      }

      if (fieldName === 'categoryId') {
        // Reset dependent fields
        this.selectedValues = {
          ...this.selectedValues,
          departmentId: '',
          documentTypeId: '',
        }
        this.departments = []
        this.documentTypes = []

        if (newValue && this.selectedValues.tenantId) {
          await this.fetchTenantDepartments(this.selectedValues.tenantId, newValue)
        }
      }

      if (fieldName === 'departmentId') {
        // Reset document type
        this.selectedValues = {
          ...this.selectedValues,
          documentTypeId: '',
        }
        this.documentTypes = []

        if (newValue && this.selectedValues.tenantId && this.selectedValues.categoryId) {
          await this.fetchTenantDocumentTypes(this.selectedValues.tenantId, this.selectedValues.categoryId, newValue)
        }
      }

      // Fetch extraction fields after any dropdown change
      await this.fetchExtractionFields()
    },
    async handleSyncNewFields() {
      this.loadingStates.syncVasionFields = true
      try {
        // CRITICAL FIX: Use proper IDs for both step mode and regular mode
        const effectiveTenantId = this.isStepMode ? this.tenantId : this.selectedValues.tenantId
        const effectiveDocumentTypeId = this.isStepMode ? this.documentTypeId : this.selectedValues.documentTypeId

        if (!effectiveTenantId || !effectiveDocumentTypeId) {
          showToast({
            title: this.$t('common.error'),
            message: 'Missing tenant ID or document type ID for field synchronization',
            type: 'error',
          })
          return
        }

        const response = await post(`/v1/tenant-document-type-fields/sync-vasion-fields?tenantId=${effectiveTenantId}&tenantDocumentTypeId=${effectiveDocumentTypeId}`)
        if (response?.data?.code === 200) {
          showToast({
            title: this.$t('common.success'),
            message: this.$t('extractionFields.messages.success.vasion_fields_synced') || 'Vasion fields synchronized successfully',
            type: 'success',
          })
          this.fetchExtractionFields()
        }
      } catch (err) {
        console.error('Failed to sync vasion fields:', err)
        showToast({
          title: this.$t('common.error'),
          message: 'Failed to sync vasion fields: ' + (err.message || 'Unknown error'),
          type: 'error',
        })
      } finally {
        this.loadingStates.syncVasionFields = false
      }
    },
    handleConfidenceChange(event, field) {
      if (!this.writePermissionMaster && !this.writePermission) {
        showToast({
          title: this.$t('common.error'),
          message: this.$t('extractionFields.messages.error.no_permission_update_fields'),
          type: 'error',
        })
        return
      }

      // Mark that user has made changes
      this.hasUserMadeChanges = true

      const value = event.target.value
      // Allow only numbers and one decimal point
      if (/^\d*\.?\d*$/.test(value)) {
        // Convert to number and validate range (0-100)
        const numValue = parseFloat(value)
        if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
          field.ml_confidence_level = numValue
          // If using Bedrock or Textract in step mode, update local list instead of calling API
          if ((this.selectedTenantUsesBedrock || this.selectedTenantUsesTextract) && this.isStepMode) {
            this.updateExtractionMappingList(field.id, field)
          } else {
            this.handleExtractionFieldUpdate(field.id, field, 'confidence')
          }
        }
      }
    },
    async handleExtractionFieldUpdate(id, payload, type) {
      this.loadingStates.updateExtractionField = true
      try {
        let response = await put(`/v1/tenant-document-type-fields/${id}`, payload)
        if (response?.data?.code === 200) {
          showToast({
            title: this.$t('common.success'),
            message: type === 'confidence' ? this.$t('extractionFields.messages.success.confidence_level_updated') : type === 'status' ? (payload.is_active ? this.$t('extractionFields.messages.success.field_enabled') : this.$t('extractionFields.messages.success.field_disabled')) : this.$t('extractionFields.messages.success.extraction_field_updated'),
            type: 'success',
          })
        } else {
          showToast({
            title: this.$t('common.error'),
            message: this.$t('extractionFields.messages.error.failed_to_update_extraction_field'),
            type: 'error',
          })
        }
      } catch (err) {
        console.error(`Failed to update extraction field`, err)
        showToast({
          title: this.$t('extractionFields.messages.error.update_error'),
          message: err.message || this.$t('extractionFields.messages.error.failed_to_update_extraction_field'),
          type: 'error',
        })
      } finally {
        this.loadingStates.updateExtractionField = false
      }
    },
    togglePageSizeDropdown() {
      this.showPageSizeDropdown = !this.showPageSizeDropdown
    },
    async selectPageSize(size) {
      this.pageSize = size
      this.page = 1
      this.showPageSizeDropdown = false
      await this.fetchExtractionFields()
    },
    async goToPreviousPage() {
      if (this.page > 1) {
        this.page--
        await this.fetchExtractionFields()
      }
    },
    async goToNextPage() {
      if (this.page < this.totalPages) {
        this.page++
        await this.fetchExtractionFields()
      }
    },
    handleFilterChange(filters) {
      this.filters = filters
      this.pageSize = 10
      this.page = 1
      this.fetchExtractionFields()
      // Implement filter logic here
    },
    handleStatusToggle(field, newValue) {
      console.log('handleStatusToggle called:', {
        fieldName: field.vasion_field_name,
        fieldId: field.id,
        currentValue: field.is_active,
        newValue: newValue,
        fieldType: typeof field.is_active,
        newValueType: typeof newValue,
      })

      if (!this.writePermissionMaster && !this.writePermission) {
        showToast({
          title: this.$t('common.error'),
          message: this.$t('extractionFields.messages.error.no_permission_update_fields'),
          type: 'error',
        })
        return
      }

      // Mark that user has made changes
      this.hasUserMadeChanges = true

      this.pendingStatusChange = {
        field: { ...field },
        newStatus: newValue,
      }
      this.showConfirmModal = true
    },
    handleDefaultCheck(field, newValue) {
      if (!this.writePermissionMaster && !this.writePermission) {
        showToast({
          title: this.$t('common.error'),
          message: this.$t('extractionFields.messages.error.no_permission_update_fields'),
          type: 'error',
        })
        return
      }

      // Mark that user has made changes
      this.hasUserMadeChanges = true

      this.pendingStatusChange = {
        field: { ...field },
        newStatus: newValue,
      }
      this.showConfirmModalForDefault = true
    },
    closeConfirmModal() {
      this.showConfirmModal = false
      this.showConfirmModalForDefault = false
      this.pendingStatusChange = null
    },
    async confirmStatusChange() {
      if (!this.pendingStatusChange) return

      const { field, newStatus } = this.pendingStatusChange

      console.log('confirmStatusChange called:', {
        fieldName: field.vasion_field_name,
        fieldId: field.id,
        newStatus: newStatus,
        newStatusType: typeof newStatus,
        isStepMode: this.isStepMode,
        usesBedrock: this.selectedTenantUsesBedrock,
      })

      const updatedField = this.extractionFields.find((f) => f.id === field.id)

      if (updatedField) {
        console.log('Before update - field.is_active:', updatedField.is_active, typeof updatedField.is_active)

        // CRITICAL FIX: Update the field in extractionFields array first
        updatedField.is_active = Boolean(newStatus)

        console.log('After update - field.is_active:', updatedField.is_active, typeof updatedField.is_active)

        // Force Vue reactivity update
        this.$forceUpdate()

        // If using Bedrock or Textract in step mode, update local list instead of calling API
        if ((this.selectedTenantUsesBedrock || this.selectedTenantUsesTextract) && this.isStepMode) {
          console.log('Updating extraction mapping list for Bedrock/Textract step mode')
          this.updateExtractionMappingList(field.id, {
            ...updatedField, // Use updated field instead of original field
            is_active: Boolean(newStatus), // Ensure boolean type
          })
        } else {
          console.log('Calling API to update extraction field')
          await this.handleExtractionFieldUpdate(
            field.id,
            {
              ...updatedField, // Use updated field instead of original field
              is_active: Boolean(newStatus), // Ensure boolean type
            },
            'status'
          )
        }
      } else {
        console.error('Could not find field to update:', field.id)
      }

      this.closeConfirmModal()
    },
    async confirmDefaultValueChange() {
      if (!this.pendingStatusChange) return

      const { field, newStatus } = this.pendingStatusChange
      const updatedField = this.extractionFields.find((f) => f.id === field.id)

      if (updatedField) {
        updatedField.default_value_required = newStatus
        // If using Bedrock or Textract in step mode, update local list instead of calling API
        if ((this.selectedTenantUsesBedrock || this.selectedTenantUsesTextract) && this.isStepMode) {
          this.updateExtractionMappingList(field.id, {
            ...field,
            default_value_required: newStatus,
          })
        } else {
          await this.handleExtractionFieldUpdate(
            field.id,
            {
              ...field,
              default_value_required: newStatus,
            },
            'default_value_required'
          )
        }
      }
      this.closeConfirmModal()
    },
    // New method to update the extraction mapping list for Bedrock flow
    // IMPORTANT: This method must preserve critical Vasion identifiers
    updateExtractionMappingList(fieldId, updatedData) {
      console.log('🗘️  updateExtractionMappingList called:', {
        fieldId,
        updatedData,
        currentListLength: this.extractionMappingList?.length || 0,
        // CRITICAL: Log is_active value and its type
        is_active_value: updatedData.is_active,
        is_active_type: typeof updatedData.is_active,
      })

      // Find the original field from extractionFields to get the correct Vasion identifiers
      const originalField = this.extractionFields.find((f) => f.id === fieldId)

      const existingIndex = this.extractionMappingList.findIndex((item) => item.id === fieldId)

      if (existingIndex >= 0) {
        // Update existing entry while preserving Vasion identifiers
        const beforeUpdate = { ...this.extractionMappingList[existingIndex] }

        this.extractionMappingList[existingIndex] = {
          ...this.extractionMappingList[existingIndex],
          ...updatedData,
          // CRITICAL: ALWAYS use _original.FieldName as the source of truth for vasion_field_key
          vasion_field_key: originalField?._original?.FieldName || originalField?.vasion_field_key || this.extractionMappingList[existingIndex].vasion_field_key,
          vasion_field_name: originalField?.vasion_field_name || this.extractionMappingList[existingIndex].vasion_field_name,
          id: fieldId, // Always preserve the ID
          // CRITICAL FIX: Ensure is_active is properly preserved as boolean
          ...(updatedData.hasOwnProperty('is_active') && {
            is_active: Boolean(updatedData.is_active),
          }),
        }
        console.log('✏️  Updated existing entry at index', existingIndex, 'for field:', originalField?.vasion_field_name, {
          before_is_active: beforeUpdate.is_active,
          after_is_active: this.extractionMappingList[existingIndex].is_active,
          is_active_type: typeof this.extractionMappingList[existingIndex].is_active,
        })
      } else {
        // Add new entry with original Vasion identifiers
        const newEntry = {
          id: fieldId,
          ...updatedData,
          // CRITICAL: ALWAYS use _original.FieldName as the source of truth for vasion_field_key
          vasion_field_key: originalField?._original?.FieldName || originalField?.vasion_field_key,
          vasion_field_name: originalField?.vasion_field_name,
          // CRITICAL FIX: Ensure is_active is properly stored as boolean
          ...(updatedData.hasOwnProperty('is_active') && {
            is_active: Boolean(updatedData.is_active),
          }),
        }

        this.extractionMappingList.push(newEntry)
        console.log('➕ Added new entry for field:', originalField?.vasion_field_name, '- new list length:', this.extractionMappingList.length, {
          is_active_value: newEntry.is_active,
          is_active_type: typeof newEntry.is_active,
        })
      }
    },
    handleSearch() {
      // Clear existing timeout
      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout)
      }

      // Set new timeout
      this.debounceTimeout = setTimeout(() => {
        this.page = 1 // Reset to first page when searching
        this.fetchExtractionFields()
      }, 300) // 300ms debounce
    },

    truncateText(text, maxLength) {
      if (!text) return ''
      if (text.length <= maxLength) return text
      return text.substring(0, maxLength) + '...'
    },

    showTooltip(event, text) {
      if (!text || text.length <= 20) return

      this.tooltipText = text
      this.tooltipVisible = true

      // Position tooltip relative to mouse
      const rect = event.target.getBoundingClientRect()
      this.tooltipStyle = {
        left: `${rect.left}px`,
        top: `${rect.bottom + 5}px`,
        position: 'fixed',
        zIndex: 9999,
      }
    },

    hideTooltip() {
      this.tooltipVisible = false
      this.tooltipText = ''
      this.tooltipStyle = {}
    },

    getFormattedDropdownValues(dropdownValues) {
      if (!dropdownValues) return ''
      try {
        const parsed = JSON.parse(dropdownValues)
        if (Array.isArray(parsed)) {
          return parsed.join(', ')
        }
        return String(dropdownValues)
      } catch (error) {
        return String(dropdownValues)
      }
    },

    showDropdownTooltip(event, dropdownValues) {
      if (!dropdownValues) return

      const formattedValues = this.getFormattedDropdownValues(dropdownValues)
      if (formattedValues.length <= 25) return

      this.dropdownTooltipText = formattedValues
      this.dropdownTooltipVisible = true

      // Position tooltip relative to mouse
      const rect = event.target.getBoundingClientRect()
      this.dropdownTooltipStyle = {
        left: `${rect.left}px`,
        top: `${rect.bottom + 5}px`,
        position: 'fixed',
        zIndex: 9999,
      }
    },

    hideDropdownTooltip() {
      this.dropdownTooltipVisible = false
      this.dropdownTooltipText = ''
      this.dropdownTooltipStyle = {}
    },

    openDropdownModal(field) {
      if (!field.vasion_drop_down_values) return

      try {
        const parsed = JSON.parse(field.vasion_drop_down_values)
        this.selectedDropdownValues = Array.isArray(parsed) ? parsed : [parsed]
      } catch (error) {
        this.selectedDropdownValues = [field.vasion_drop_down_values]
      }

      this.selectedFieldName = field.vasion_field_name
      this.showDropdownModal = true
    },

    closeDropdownModal() {
      this.showDropdownModal = false
      this.selectedDropdownValues = []
      this.selectedFieldName = ''
    },

    // Helper methods for step mode integration
    getDocumentTypeName(type) {
      // For Bedrock mode, use blueprint schema class, otherwise use documentTypeName

      if (this.selectedTenantUsesBedrock && this.blueprintSchema?.class && type === 'class') {
        const formattedClass = this.formatClassName(this.blueprintSchema.class)
        console.log('BDAExtraction: Using blueprint class as document type:', this.blueprintSchema.class, '→', formattedClass)
        return formattedClass
      } else {
        console.log('BDAExtraction: Using documentTypeName:', this.documentTypeName)
        return this.documentTypeName || 'New Document Type'
      }
    },

    // Map Control field values to readable data types
    getFieldDataType(control) {
      const controlTypes = {
        1: 'Text',
        2: 'Dropdown',
        3: 'Date',
        6: 'Number',
        11: 'Currency',
        // Add more mappings as needed
      }
      return controlTypes[control] || 'Unknown'
    },
    getSourceDocumentName() {
      return this.sourceDocumentName || 'No document selected'
    },

    // Format class name from snake_case to Title Case
    formatClassName(name) {
      if (!name) return ''
      return name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    },

    // Apply preset field mappings to extraction fields after they're loaded
    applyPresetFieldMappings() {
      if (!this.presetFieldMappings || this.presetFieldMappings.length === 0) {
        console.log('BDAExtraction: No preset field mappings to apply')
        return
      }

      if (!this.extractionFields || this.extractionFields.length === 0) {
        console.log('BDAExtraction: No extraction fields loaded yet, cannot apply mappings')
        return
      }

      console.log('=== PRESET FIELD MAPPINGS DEBUG START ===')
      console.log('BDAExtraction: presetFieldMappings count:', this.presetFieldMappings.length)
      console.log('BDAExtraction: extractionFields count:', this.extractionFields.length)

      // Debug: Show first few preset mappings structure
      console.log('BDAExtraction: First 3 preset mappings:')
      this.presetFieldMappings.slice(0, 3).forEach((preset, idx) => {
        console.log(`Preset ${idx + 1}:`, {
          id: preset.id,
          vasion_field_id: preset.vasion_field_id,
          vasion_field_key: preset.vasion_field_key,
          vasion_field_name: preset.vasion_field_name,
          bedrock_field: preset.bedrock_field,
          selected_bedrock_field: preset.selected_bedrock_field,
        })
      })

      // Debug: Show first few extraction fields structure
      console.log('BDAExtraction: First 3 extraction fields:')
      this.extractionFields.slice(0, 3).forEach((field, idx) => {
        console.log(`Field ${idx + 1}:`, {
          id: field.id,
          vasion_field_name: field.vasion_field_name,
          vasion_field_key: field.vasion_field_key,
          selected_bedrock_field: field.selected_bedrock_field,
        })
      })

      let matchedCount = 0
      let unmatchedFields = []

      // Map preset field mappings to extraction fields by matching vasion_field_id or vasion_field_key
      this.extractionFields.forEach((field, index) => {
        // Try to find matching preset mapping by ID first, then by key
        const presetMapping = this.presetFieldMappings.find((preset) => {
          const idMatch = preset.vasion_field_id === field.id
          const keyMatch = preset.vasion_field_key === field.vasion_field_key
          const dbIdMatch = preset.id === field.id

          if (idMatch || keyMatch || dbIdMatch) {
            console.log(`✅ MATCH FOUND for '${field.vasion_field_name}':`, {
              matchType: idMatch ? 'vasion_field_id' : keyMatch ? 'vasion_field_key' : 'database_id',
              presetId: preset.id,
              presetVasionFieldId: preset.vasion_field_id,
              presetKey: preset.vasion_field_key,
              fieldId: field.id,
              fieldKey: field.vasion_field_key,
            })
            return true
          }
          return false
        })

        if (presetMapping) {
          matchedCount++
          console.log(`BDAExtraction: Found preset mapping for field '${field.vasion_field_name}':`, {
            presetBedrock: presetMapping.bedrock_field,
            presetSelectedBedrock: presetMapping.selected_bedrock_field,
            presetConfidence: presetMapping.ml_confidence_level,
            presetActive: presetMapping.is_active,
          })

          // Apply preset values to the extraction field
          if (presetMapping.selected_bedrock_field) {
            field.selected_bedrock_field = presetMapping.selected_bedrock_field
            console.log(`  → Set selected_bedrock_field to: ${presetMapping.selected_bedrock_field}`)
          }
          if (presetMapping.bedrock_field) {
            field.selected_bedrock_field = presetMapping.bedrock_field
            console.log(`  → Set selected_bedrock_field to: ${presetMapping.bedrock_field}`)
          }
          if (presetMapping.ml_confidence_level !== undefined) {
            field.ml_confidence_level = presetMapping.ml_confidence_level
            console.log(`  → Set ml_confidence_level to: ${presetMapping.ml_confidence_level}`)
          }
          if (presetMapping.is_active !== undefined) {
            field.is_active = presetMapping.is_active
            console.log(`  → Set is_active to: ${presetMapping.is_active}`)
          }
          if (presetMapping.default_value_required !== undefined) {
            field.default_value_required = presetMapping.default_value_required
            console.log(`  → Set default_value_required to: ${presetMapping.default_value_required}`)
          }

          // CRITICAL: Update the extraction mapping list to maintain persistence
          this.updateExtractionMappingList(field.id, field)

          console.log(`BDAExtraction: ✅ Applied preset values to field '${field.vasion_field_name}'`)
        } else {
          unmatchedFields.push({
            name: field.vasion_field_name,
            id: field.id,
            key: field.vasion_field_key,
          })
          console.log(`❌ NO MATCH for field '${field.vasion_field_name}' (id: ${field.id}, key: ${field.vasion_field_key})`)
        }
      })

      console.log(`=== PRESET MAPPING RESULTS ===`)
      console.log(`Total fields: ${this.extractionFields.length}`)
      console.log(`Matched fields: ${matchedCount}`)
      console.log(`Unmatched fields: ${unmatchedFields.length}`)
      if (unmatchedFields.length > 0) {
        console.log('Unmatched fields:', unmatchedFields)
      }
      console.log('=== PRESET FIELD MAPPINGS DEBUG END ===')
    },

    // Handle document type name changes
    handleDocumentTypeNameChange() {
      // Mark that user has made changes
      this.hasUserMadeChanges = true
      // Validation can be added here if needed
      console.log('Document type name changed to:', this.editableDocumentTypeName)
    },

    // Handle completion for step mode
    handleComplete() {
      if (this.isStepMode) {
        // If using Bedrock OR Textract, pass the extraction mapping list and selected Vasion Form ID
        if (this.selectedTenantUsesBedrock || this.selectedTenantUsesTextract) {
          // CRITICAL: Merge field states while preserving original Vasion field keys
          const finalExtractionList = this.extractionFields.map((field) => {
            const mappingUpdate = this.extractionMappingList.find((item) => item.id === field.id)

            if (mappingUpdate) {
              // IMPORTANT: Preserve critical Vasion identifiers from original field
              // Never let user updates override these core Vasion integration properties
              const mergedField = {
                ...field, // Start with original Vasion field data
                ...mappingUpdate, // Apply user updates (confidence, active, etc.)
                // FORCE preserve these critical Vasion properties
                vasion_field_key: field.vasion_field_key, // NEVER override
                vasion_field_name: field.vasion_field_name, // NEVER override
                id: field.id, // NEVER override
                _original: field._original, // NEVER override
              }

              // CRITICAL FIX: Ensure bedrock_field and ai_method_field are populated correctly
              // Backend uses bedrock_field OR ai_method_field to store the mapped value
              if (this.selectedTenantUsesBedrock) {
                // For Bedrock: bedrock_field should be set from selected_bedrock_field
                if (mergedField.selected_bedrock_field) {
                  mergedField.bedrock_field = mergedField.selected_bedrock_field
                  mergedField.ai_method_field = mergedField.selected_bedrock_field
                }
              } else if (this.selectedTenantUsesTextract) {
                // For Textract: bedrock_field should be set from selected_vasion_field_key
                if (mergedField.selected_vasion_field_key) {
                  mergedField.bedrock_field = mergedField.selected_vasion_field_key
                  mergedField.ai_method_field = mergedField.selected_vasion_field_key
                }
              }

              // Debug logging to ensure field keys are preserved
              console.log(`Merging field '${field.vasion_field_name}':`, {
                originalKey: field.vasion_field_key,
                finalKey: mergedField.vasion_field_key,
                keyPreserved: field.vasion_field_key === mergedField.vasion_field_key,
                bedrockField: mergedField.bedrock_field,
                aiMethodField: mergedField.ai_method_field,
              })

              return mergedField
            }
            return field
          })

          // Final validation before sending to parent
          console.log('=== BDAExtraction Final Extraction List Validation ===')
          finalExtractionList.forEach((field, index) => {
            console.log(`Field ${index + 1}: ${field.vasion_field_name}`, {
              vasion_field_key: field.vasion_field_key,
              selected_vasion_field_key: field.selected_vasion_field_key,
              selected_bedrock_field: field.selected_bedrock_field,
              bedrock_field: field.bedrock_field,
              ai_method_field: field.ai_method_field,
              hasKey: !!field.vasion_field_key,
              keyLength: field.vasion_field_key?.length || 0,
            })

            if (!field.vasion_field_key) {
              console.error(`❌ CRITICAL: Field '${field.vasion_field_name}' missing vasion_field_key!`)
            }

            // CRITICAL: Log Textract field mapping issue
            if (field.selected_vasion_field_key && (!field.bedrock_field || field.bedrock_field === '')) {
              console.error(`❌ CRITICAL: Field '${field.vasion_field_name}' has selected_vasion_field_key='${field.selected_vasion_field_key}' but bedrock_field is empty!`)
            }
          })

          console.log('🚀 EMITTING to parent - Full first 3 fields:', JSON.stringify(finalExtractionList.slice(0, 3), null, 2))

          this.$emit('complete', {
            extractionMappingList: finalExtractionList,
            vasionFormId: this.selectedValues.vasionFormId,
            editedDocumentTypeName: this.editableDocumentTypeName,
          })
        } else {
          this.$emit('complete')
        }
      }
    },

    // Save current state before navigation (for persistence)
    saveCurrentState() {
      return {
        selectedVasionFormId: this.selectedValues.vasionFormId,
        extractionMappingList: this.extractionMappingList,
      }
    },
  },
  watch: {
    'selectedValues.tenantId': {
      handler(newValue) {
        this.handleFieldChange('tenantId', newValue)
      },
      immediate: true,
    },
    'selectedValues.categoryId': {
      handler(newValue) {
        this.handleFieldChange('categoryId', newValue)
      },
    },
    'selectedValues.departmentId': {
      handler(newValue) {
        this.handleFieldChange('departmentId', newValue)
      },
    },
    'selectedValues.documentTypeId': {
      handler(newValue) {
        const selectedDocType = this.documentTypes.find((doc) => doc.id === newValue)
        if (selectedDocType) {
          this.selectedValues.vasionFormId = selectedDocType.vasion_form_id
          this.selectedDocType = selectedDocType
          this.handleSyncAiMethodFields()
        }
        this.fetchExtractionFields()
      },
    },
    'selectedValues.vasionFormId': {
      handler(newFormId, oldFormId) {
        console.log('BDAExtraction: vasionFormId watcher - old:', oldFormId, 'new:', newFormId, 'fieldsLoaded:', this.fieldsLoaded)

        // Only proceed if the form actually changed
        if (newFormId !== oldFormId) {
          if (!newFormId) {
            // Form ID cleared, clear fields
            console.log('BDAExtraction: Form ID cleared, clearing fields')
            this.extractionFields = []
            this.fieldsLoaded = false
            this.hasUserMadeChanges = false
          } else if (newFormId) {
            // Form ID changed to a valid value
            if (this.fieldsLoaded && this.hasUserMadeChanges && oldFormId) {
              // User has unsaved changes and is switching forms - show confirmation
              console.log('BDAExtraction: Form ID changed with unsaved changes, asking for confirmation')
              console.log('BDAExtraction: User confirmed form change, fetching new fields')
              this.hasUserMadeChanges = false // Reset change flag
              this.fieldsLoaded = false // Reset loaded flag
              this.fetchExtractionFields()
              // console.log('BDAExtraction: User cancelled form change, reverting to old form')
              // // Revert to old form ID without triggering watcher again
              // this.$nextTick(() => {
              //   this.selectedValues.vasionFormId = oldFormId
              // })
            } else {
              // No unsaved changes or first load - fetch fields normally
              console.log('BDAExtraction: Form ID changed, fetching extraction fields')
              this.fieldsLoaded = false // Reset to ensure fresh data
              this.fetchExtractionFields()
            }
          }
        } else {
          console.log('BDAExtraction: Form ID unchanged, skipping fetch')
        }
      },
    },
    // Watch for blueprintSchema prop changes
    blueprintSchema: {
      handler(newSchema) {
        // Blueprint schema updated - component will reactively update
      },
      deep: true,
      immediate: true,
    },
    presetVasionFormId: {
      handler(newFormId) {
        console.log('BDAExtraction: presetVasionFormId watcher triggered with:', newFormId)
        if (newFormId) {
          // Wait for Vasion forms to be loaded before setting form ID
          this.$nextTick(() => {
            if (this.vasionForms.length > 0) {
              console.log('BDAExtraction: Setting vasionFormId to', newFormId)
              this.selectedValues.vasionFormId = newFormId
            } else {
              console.log('BDAExtraction: Vasion forms not loaded yet, will retry...')
              // Retry after a short delay if forms aren't loaded yet
              setTimeout(() => {
                if (this.vasionForms.length > 0) {
                  console.log('BDAExtraction: Retry - Setting vasionFormId to', newFormId)
                  this.selectedValues.vasionFormId = newFormId
                }
              }, 500)
            }
          })
        }
      },
      immediate: true,
    },
    presetFieldMappings: {
      handler(newMappings) {
        console.log('BDAExtraction: presetFieldMappings watcher triggered with:', newMappings)
        console.log('BDAExtraction: Current extractionMappingList count:', this.extractionMappingList?.length || 0)
        console.log('BDAExtraction: Current state - isInitialLoad:', this.isInitialLoad, 'hasUserMadeChanges:', this.hasUserMadeChanges)

        if (newMappings && newMappings.length > 0) {
          // CRITICAL FIX: Always apply preset mappings when they exist
          // These represent user selections from previous navigation that need to be restored
          console.log('📋 BDAExtraction: Applying PRESET mappings (user selections from previous step):', newMappings.length)

          // Deep clone to avoid reference issues
          this.extractionMappingList = JSON.parse(JSON.stringify(newMappings))

          // Mark that we have user changes to prevent further overwrites
          this.hasUserMadeChanges = true

          console.log('✅ BDAExtraction: Successfully restored', this.extractionMappingList.length, 'field mappings')
        } else {
          console.log('🆕 BDAExtraction: No preset mappings provided')
        }
      },
      immediate: true,
    },
    // Watch for vasionForms to be loaded to apply preset form ID
    vasionForms: {
      handler(newForms) {
        console.log('BDAExtraction: vasionForms loaded:', newForms.length)
        // If we have a preset form ID and forms are now loaded, apply it
        if (this.presetVasionFormId && newForms.length > 0 && !this.selectedValues.vasionFormId) {
          console.log('BDAExtraction: Applying preset vasion form ID after forms loaded:', this.presetVasionFormId)
          this.selectedValues.vasionFormId = this.presetVasionFormId
        }
      },
      immediate: false,
    },
  },
}
</script>

<style scoped>
.confidence-input {
  width: 80px;
  padding: 4px 8px;
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  font-family: Archivo;
  font-size: 14px;
  text-align: right;
}

.confidence-input:focus {
  outline: none;
  border-color: var(--primary-color);
}

.confidence-input::-webkit-outer-spin-button,
.confidence-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Vasion Field Key Tooltip Styles */
.vasion-field-key-container {
  position: relative;
  display: inline-block;
}

.vasion-field-key-text {
  display: inline-block;
  max-width: 100%;
}

.vasion-field-key-truncated {
  cursor: pointer;
  /* color: #007bff; */
  text-decoration: underline;
}

.vasion-field-key-tooltip {
  background-color: #333;
  color: white;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 12px;
  max-width: 300px;
  word-wrap: break-word;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  pointer-events: none;
}

/* Dropdown Values Tooltip Styles */
.dropdown-values-container {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.dropdown-values-text {
  display: inline-block;
  max-width: 100%;
}

.dropdown-values-truncated {
  cursor: pointer;
  text-decoration: underline;
}

.dropdown-values-tooltip {
  background-color: #333;
  color: white;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 12px;
  max-width: 300px;
  word-wrap: break-word;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  pointer-events: none;
}

.dropdown-view-icon {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;
}

.dropdown-view-icon:hover {
  background-color: #f0f9fd;
}

.dropdown-view-icon svg {
  transition: transform 0.2s ease;
}

.dropdown-view-icon:hover svg {
  transform: scale(1.1);
}

.searchBar {
  display: flex;
  align-items: center;
  width: 386px;
  height: 36px;
  border-radius: 6px;
  border: 1px solid #dadada;
  background: #fff;
  padding: 0 10px;
  gap: 10px;
}

.searchInput {
  border: none;
  outline: none;
  width: 100%;
  font-family: Archivo;
  font-size: 14px;
  font-weight: 400;
  color: #8f969b;
}

.button-group {
  margin-top: 20px;
  display: flex;
  gap: 20px;
}

.cancel-btn {
  padding: 10px 32px;
  background: transparent;
  border: 1px solid var(--primary-color);
  color: var(--primary-color);
  font-family: Archivo;
  font-weight: 500;
  border-radius: 6px;
  cursor: pointer;
}

.save-btn {
  padding: 10px 32px;
  background: var(--primary-color);
  color: white;
  font-family: Archivo;
  font-weight: 500;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.form-field label {
  color: #8b8888;
  font-family: Archivo;
  font-size: 18px;
  font-weight: 400;
  display: block;
  margin-bottom: 6px;
}

.form-field input,
.form-field select {
  width: 100%;
  height: 47px;
  padding: 10px 12px;
  border-radius: 6px;
  border: 1px solid #dbdbdb;
  font-family: Archivo;
  font-size: 16px;
  color: #32393e;
}

.newPageTitle {
  color: var(--primary-color);
  font-family: Archivo;
  font-size: 28px;
  font-weight: 500;
  line-height: 150%;
}

.pageDiv {
  position: relative;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #ccc;
  padding: 6px 12px;
  border-radius: 4px;
  background: #fff;
}

.pageSizeDropdownNew {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 1000;
  background: #fff;
  border: 1px solid #ccc;
  border-radius: 4px;
  margin-top: 4px;
  list-style: none;
  padding: 4px 0;
  width: 100%;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

.pageSizeDropdownNew li {
  padding: 6px 12px;
  cursor: pointer;
  font-family: 'Archivo', sans-serif;
  font-size: 14px;
  color: #5c656c;
}

.pageSizeDropdownNew li:hover {
  background: #f0f0f0;
}

.custom-extraction-field-top-bar {
  margin-bottom: 20px;
}

.extraction-top-bar-title {
  color: var(--primary-color);
  text-align: center;
  font-family: Archivo;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
}

.dropdown-container {
  width: 100%;
  box-sizing: border-box;
  overflow-x: auto;
}

.dropdown-grid {
  display: flex;
  flex-wrap: nowrap;
  gap: 20px;
  margin-top: 20px;
  margin-bottom: 25px;
  width: 100%;
}

.form-field {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.form-field label {
  margin-bottom: 6px;
  font-family: Archivo;
  font-size: 14px;
  color: #999999;
  white-space: nowrap;
  /* Prevents wrapping */
  overflow: hidden;
  /* Hides overflow */
  text-overflow: ellipsis;
  /* Shows ellipsis */
  display: block;
  /* Required for ellipsis to work */
}

.custom-select {
  width: 100%;
  min-width: 0;
  height: 47px;
  padding: 10px 12px;
  border-radius: 6px;
  border: 1px solid #dbdbdb;
  font-family: Archivo;
  font-size: 16px;
  color: #32393e;
  background-color: white;
  cursor: pointer;
  text-overflow: ellipsis;
}

.custom-select:focus {
  outline: none;
  border-color: var(--primary-color);
}

.extraction-top-bar-title {
  margin-bottom: 20px;
}

.sync-icon-wrapper {
  cursor: pointer;
  display: flex;
  margin-top: 16px;
  justify-content: end;
  align-items: end;
}

.sync-icon {
  font-size: 24px;
  cursor: pointer;
  color: var(--primary-color);
}

.sync-icon-header {
  font-size: 16px;
  cursor: pointer;
  color: var(--primary-color);
}

.sync-icon:hover {
  color: #0d6efd;
  /* Optional hover color */
}

.blueprint-btn {
  padding: 8px 12px;
  font-size: 0.93rem;
  font-family: 'Archivo', Arial, sans-serif;
  min-height: unset;
  line-height: 1;
  background: var(--primary-color);
  color: #fff;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  transition: background 0.2s;
}

.blueprint-btn:hover:not(:disabled) {
  background: var(--primary-color);
  opacity: 0.9;
}

.blueprint-btn:disabled,
.blueprint-btn-disabled {
  background: #ccc !important;
  cursor: not-allowed !important;
  opacity: 0.6 !important;
}

.blueprint-btn:disabled:hover,
.blueprint-btn-disabled:hover {
  background: #ccc !important;
  opacity: 0.6 !important;
}

.header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.document-type-input:focus {
  outline: none;
  border-color: var(--primary-color);
}
</style>
